import { PlaygroundImage } from "../../../../models/playground";
import { Language } from "../LanguagesDropdown/LanguagesDropdown";

export interface CodeGenerationParams {
	url: string;
	token: string;
	image: PlaygroundImage;
	language: Language;
}

export interface PayloadParams {
	structuredPrompt: string;
	seed: number;
	aspectRatio: string;
	steps: number;
	guidanceScale: number;
	image?: string;
}

export interface GeneratePayloadParams extends PayloadParams {
	prompt: string;
}

export interface RefinePayloadParams extends PayloadParams {
	refinePrompt: string;
}

const extractImageParams = (image: PlaygroundImage): PayloadParams => ({
	structuredPrompt: image.structured_prompt || "string",
	seed: image.seed || 123456789,
	aspectRatio: image.aspect_ratio || "16:9",
	steps: (image.config as any)?.steps_num || 20,
	guidanceScale: (image.config as any)?.text_guidance_scale || 7.5,
	image: image.ref_image_url,
});

// Helper function to escape special characters in JSON strings
const escapeJsonString = (str: string): string => {
	return str
		.replace(/\\/g, "\\\\") // Escape backslashes first
		.replace(/"/g, '\\"') // Escape double quotes
		.replace(/\n/g, "\\n") // Escape newlines
		.replace(/\r/g, "\\r") // Escape carriage returns
		.replace(/\t/g, "\\t"); // Escape tabs
};

// Helper to format value for code generation - escape strings properly
const formatValue = (value: any, escape: boolean = true): string => {
	if (typeof value === "string") {
		return escape ? `"${escapeJsonString(value)}"` : `"${value}"`;
	}
	return String(value);
};

const generatePythonCode = (payload: Record<string, any>, url: string, token: string): string => {
	const payloadStr = Object.entries(payload)
		.map(([key, value]) => `    "${key}": ${formatValue(value)}`)
		.join(",\n");

	return `import requests

url = "${url}"

payload = {
${payloadStr}
}

headers = {
    "Content-Type": "application/json",
    "api_token": "${token}"
}

response = requests.post(url, json=payload, headers=headers)
data = response.json()
print(data)`;
};

const generateJavaScriptCode = (payload: Record<string, any>, url: string, token: string): string => {
	const payloadStr = Object.entries(payload)
		.map(([key, value]) => `        ${key}: ${formatValue(value)}`)
		.join(",\n");

	return `const response = await fetch("${url}", {
    method: "POST",
    headers: {
        "Content-Type": "application/json",
        "api_token": "${token}"
    },
    body: JSON.stringify({
${payloadStr}
    })
});

const data = await response.json();
console.log(data);`;
};

const generateNodejsCode = (payload: Record<string, any>, url: string, token: string): string => {
	const payloadStr = Object.entries(payload)
		.map(([key, value]) => `    ${key}: ${formatValue(value)}`)
		.join(",\n");

	return `const axios = require('axios');

const response = await axios.post("${url}", {
${payloadStr}
}, {
    headers: {
        "Content-Type": "application/json",
        "api_token": "${token}"
    }
});

console.log(response.data);`;
};

const generateCurlCode = (payload: Record<string, any>, url: string, token: string): string => {
	// Build payload object with properly formatted values
	const formattedPayload: Record<string, any> = {};
	Object.entries(payload).forEach(([key, value]) => {
		formattedPayload[key] = value;
	});

	const jsonBody = JSON.stringify(formattedPayload, null, 2);
	const safeBody = jsonBody.replace(/'/g, "'\"'\"'");
	return `curl -X POST "${url}" \\
	-H "Content-Type: application/json" \\
	-H "api_token: ${token}" \\
	-d '${safeBody}'`;
};

const generateJavaCode = (payload: Record<string, any>, url: string, token: string): string => {
	const jsonBody = JSON.stringify(payload);
	// Double escape for Java string literal
	const escapedJson = jsonBody.replace(/\\/g, "\\\\\\\\").replace(/"/g, '\\\\"');

	return `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;

HttpClient client = HttpClient.newHttpClient();
HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("${url}"))
    .header("Content-Type", "application/json")
    .header("api_token", "${token}")
    .POST(HttpRequest.BodyPublishers.ofString(
        "${escapedJson}"
    ))
    .build();

HttpResponse<String> response = client.send(request, HttpResponse.BodyHandlers.ofString());
System.out.println(response.body());`;
};

const generateCsharpCode = (payload: Record<string, any>, url: string, token: string): string => {
	const jsonBody = JSON.stringify(payload);
	// Use verbatim string (@"") and escape quotes by doubling them
	const escapedJson = jsonBody.replace(/"/g, '""');

	return `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;

var client = new HttpClient();
client.DefaultRequestHeaders.Add("api_token", "${token}");

var json = @"${escapedJson}";
var content = new StringContent(json, Encoding.UTF8, "application/json");

var response = await client.PostAsync("${url}", content);
var result = await response.Content.ReadAsStringAsync();
Console.WriteLine(result);`;
};

const generatePhpCode = (payload: Record<string, any>, url: string, token: string): string => {
	const jsonBody = JSON.stringify(payload);
	// Escape single quotes for PHP single-quoted string
	const escapedJson = jsonBody.replace(/\\/g, "\\\\").replace(/'/g, "\\'");

	return `<?php
$url = "${url}";
$data = json_decode('${escapedJson}', true);

$options = [
    "http" => [
        "header" => "Content-Type: application/json\\r\\napi_token: ${token}\\r\\n",
        "method" => "POST",
        "content" => json_encode($data)
    ]
];

$context = stream_context_create($options);
$result = file_get_contents($url, false, $context);
echo $result;
?>`;
};

const generateGoCode = (payload: Record<string, any>, url: string, token: string): string => {
	const jsonBody = JSON.stringify(payload);
	// Escape for Go string literal
	const escapedJson = jsonBody.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

	return `package main

import (
    "bytes"
    "fmt"
    "net/http"
)

func main() {
    url := "${url}"
    jsonData := []byte("${escapedJson}")
    
    req, _ := http.NewRequest("POST", url, bytes.NewBuffer(jsonData))
    req.Header.Set("Content-Type", "application/json")
    req.Header.Set("api_token", "${token}")
    
    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()
    
    fmt.Println(resp.Status)
}`;
};

const codeGenerators: Record<Language, (payload: Record<string, any>, url: string, token: string) => string> = {
	python: generatePythonCode,
	javascript: generateJavaScriptCode,
	nodejs: generateNodejsCode,
	curl: generateCurlCode,
	java: generateJavaCode,
	csharp: generateCsharpCode,
	php: generatePhpCode,
	go: generateGoCode,
};

export const generateRegeneratePayload = (imageParams: PayloadParams): Record<string, any> => ({
	structured_prompt: imageParams.structuredPrompt,
	seed: imageParams.seed,
	aspect_ratio: imageParams.aspectRatio,
	steps: imageParams.steps,
	guidance_scale: imageParams.guidanceScale,
});

export const generateGeneratePayload = (imageParams: PayloadParams, prompt: string): Record<string, any> => {
	const payload: Record<string, any> = {
		prompt,
		aspect_ratio: imageParams.aspectRatio,
		steps: imageParams.steps,
		guidance_scale: imageParams.guidanceScale,
	};

	if (imageParams.image) {
		payload.image = imageParams.image;
	}

	return payload;
};

export const generateRefinePayload = (imageParams: PayloadParams, refinePrompt: string): Record<string, any> => ({
	prompt: refinePrompt,
	structured_prompt: imageParams.structuredPrompt,
	seed: imageParams.seed,
	aspect_ratio: imageParams.aspectRatio,
	steps: imageParams.steps,
	guidance_scale: imageParams.guidanceScale,
});

export { codeGenerators, extractImageParams };
